import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Inject,
} from '@angular/core';
import { trigger, style, transition, animate } from '@angular/animations';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AIChatbotService } from '../../services/ai-chatbot/ai-chatbot.service';
import { Subscription } from 'rxjs';
import { ModalService } from 'src/app/components/modals/modal.service';

@Component({
  selector: 'ai-chatbot',
  templateUrl: './ai-chatbot.component.html',
  styleUrl: './ai-chatbot.component.scss',
  standalone: false,
  animations: [
    trigger('slideInUp', [
      transition('void => true', [
        style({
          opacity: 0,
          transform: 'translateY(20px)',
        }),
        animate(
          '400ms ease-out',
          style({
            opacity: 1,
            transform: 'translateY(0)',
          })
        ),
      ]),
    ]),
  ],
})
export class AiChatbotComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('chatContainer', { static: false }) chatContainer!: ElementRef;

  selectedTemplateIdx: number | null = null;

  initialPrompt: string;

  private scrollChatToBottomSubscription: Subscription;



  constructor(
    public aiChatbotService: AIChatbotService,
    private modalService: ModalService,
    private dialogRef: MatDialogRef<AiChatbotComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.initialPrompt = data?.initialPrompt;
  }

  ngOnInit(): void {
    // Initialize session if it doesn't exist
    if (!this.aiChatbotService.currentSession) {
      this.aiChatbotService.startNewSession();
    }

    this.subscribeToScrollChat();
  }

  ngAfterViewInit(): void {
    this.scrollChatToBottom();
  }

  private subscribeToScrollChat(): void {
    // Unsubscribe from previous subscription if it exists
    if (this.scrollChatToBottomSubscription) {
      this.scrollChatToBottomSubscription.unsubscribe();
    }

    this.scrollChatToBottomSubscription =
      this.aiChatbotService.scrollChatToBottom$.subscribe(
        (options: { onlyIfAtBottom?: boolean }) => {
          this.scrollChatToBottom(options.onlyIfAtBottom);
        }
      );
  }

  refreshSubscription(): void {
    this.subscribeToScrollChat();
    setTimeout(() => {
      this.scrollChatToBottom();
    }, 100);
  }

  async restartSession(): Promise<void> {
    try {
      await this.modalService.confirm(
        'Start a New Chat?',
        'Starting a new chat will clear your current conversation. This action cannot be undone. Are you sure you want to continue?',
        'Start New Chat',
        'Cancel',
        'messageInput'
      );
    } catch (error) {
      return;
    }

    this.aiChatbotService.restartSession();
    this.refreshSubscription();
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.scrollChatToBottomSubscription) {
      this.scrollChatToBottomSubscription.unsubscribe();
      this.scrollChatToBottomSubscription = null;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  scrollChatToBottom = (onlyIfAtBottom: boolean = false) => {
    if (this.chatContainer) {
      setTimeout(() => {
        const element = this.chatContainer.nativeElement;

        // Only scroll if !onlyIfAtBottom or the user is already at or near the bottom
        if (onlyIfAtBottom) {
          const isAtBottom =
            element.scrollTop + element.clientHeight >=
            element.scrollHeight - 10;
          if (!isAtBottom) return;
        }

        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth',
        });
        this.cdr.detectChanges();
      });
    }
  };
}
